import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';

import Icon from '../../../components/AppIcon';

const CancellationModal = ({ isOpen, onClose, onConfirm, subscription, processing }) => {
  const [step, setStep] = useState('survey'); // survey, retention, confirm
  const [selectedReason, setSelectedReason] = useState('');
  const [feedback, setFeedback] = useState('');
  const [acceptedOffer, setAcceptedOffer] = useState(false);

  const cancellationReasons = [
    { value: 'too_expensive', label: 'Too expensive', icon: 'DollarSign' },
    { value: 'not_using', label: 'Not using it enough', icon: 'Clock' },
    { value: 'missing_features', label: 'Missing features I need', icon: 'AlertCircle' },
    { value: 'switching_service', label: 'Switching to another service', icon: 'RefreshCw' },
    { value: 'technical_issues', label: 'Technical issues', icon: 'AlertTriangle' },
    { value: 'other', label: 'Other reason', icon: 'MoreHorizontal' }
  ];

  const handleReasonSelect = (reason) => {
    setSelectedReason(reason);
  };

  const handleSurveyNext = () => {
    if (!selectedReason) {
      alert('Please select a cancellation reason');
      return;
    }
    setStep('retention');
  };

  const handleAcceptOffer = () => {
    setAcceptedOffer(true);
    onConfirm(selectedReason, feedback, true);
  };

  const handleDeclineOffer = () => {
    setStep('confirm');
  };

  const handleFinalCancel = () => {
    onConfirm(selectedReason, feedback, false);
  };

  const handleClose = () => {
    setStep('survey');
    setSelectedReason('');
    setFeedback('');
    setAcceptedOffer(false);
    onClose();
  };

  const planName = subscription?.subscription_plans?.name || 'Free';
  const currentPrice = subscription?.billing_cycle === 'annual' 
    ? subscription?.subscription_plans?.annual_price 
    : subscription?.subscription_plans?.price;
  const discountedPrice = currentPrice ? (currentPrice * 0.8)?.toFixed(2) : '0.00';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={step === 'survey' ? 'We\'re sorry to see you go' : step === 'retention' ? 'Wait! Special Offer' : 'Confirm Cancellation'}
      description={step === 'survey' ? 'Help us improve by telling us why you\'re canceling' : step === 'retention' ? 'Before you go, we have a special offer for you' : 'Are you sure you want to cancel your subscription?'}
      type={step === 'retention' ? 'warning' : 'info'}
      icon={step === 'survey' ? 'MessageSquare' : step === 'retention' ? 'Gift' : 'AlertTriangle'}
      showCloseButton={!processing}
      closeOnOverlayClick={!processing}
      actions={null}
    >
      {step === 'survey' && (
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Why are you canceling?</label>
            <div className="grid grid-cols-1 gap-2">
              {cancellationReasons?.map((reason) => (
                <button
                  key={reason?.value}
                  onClick={() => handleReasonSelect(reason?.value)}
                  className={`
                    flex items-center gap-3 p-3 rounded-lg border transition-all
                    ${selectedReason === reason?.value
                      ? 'border-primary bg-primary/5 text-foreground'
                      : 'border-border hover:border-primary/50 text-muted-foreground hover:text-foreground'
                    }
                  `}
                >
                  <Icon name={reason?.icon} size={18} />
                  <span className="text-sm font-medium">{reason?.label}</span>
                  {selectedReason === reason?.value && (
                    <Icon name="CheckCircle" size={18} className="ml-auto text-primary" />
                  )}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="feedback" className="text-sm font-medium text-foreground mb-2 block">
              Additional feedback (optional)
            </label>
            <textarea
              id="feedback"
              value={feedback}
              onChange={(e) => setFeedback(e?.target?.value)}
              placeholder="Tell us more about your experience..."
              rows={4}
              className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 resize-none"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              size="md"
              onClick={handleClose}
              disabled={processing}
              fullWidth
            >
              Keep Subscription
            </Button>
            <Button
              variant="default"
              size="md"
              onClick={handleSurveyNext}
              disabled={!selectedReason || processing}
              fullWidth
            >
              Continue
            </Button>
          </div>
        </div>
      )}
      {step === 'retention' && (
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 rounded-lg p-6 border border-green-500/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <Icon name="Gift" size={24} className="text-green-500" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-foreground">20% Off for 3 Months!</h4>
                <p className="text-sm text-muted-foreground">Exclusive retention offer</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-foreground">${discountedPrice}</span>
                <span className="text-sm text-muted-foreground line-through">${currentPrice?.toFixed(2)}</span>
                <span className="text-sm text-green-500 font-medium">per month</span>
              </div>

              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-foreground">
                  <Icon name="Check" size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Keep all your {planName} features</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground">
                  <Icon name="Check" size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Save ${(currentPrice - parseFloat(discountedPrice))?.toFixed(2)}/month for 3 months</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground">
                  <Icon name="Check" size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Cancel anytime, no commitment</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-sm text-muted-foreground">
              <Icon name="Info" size={14} className="inline mr-1" />
              This offer will be automatically applied to your next 3 billing cycles. After that, your subscription will return to the regular price.
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              size="md"
              onClick={handleDeclineOffer}
              disabled={processing}
              fullWidth
            >
              No Thanks
            </Button>
            <Button
              variant="success"
              size="md"
              onClick={handleAcceptOffer}
              loading={processing}
              iconName="Gift"
              iconPosition="left"
              fullWidth
            >
              Accept Offer
            </Button>
          </div>
        </div>
      )}
      {step === 'confirm' && (
        <div className="space-y-4">
          <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/20">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">
                  Your {planName} subscription will be canceled at the end of your billing period.
                </p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• You'll lose access to all premium features</li>
                  <li>• Your agents will be limited to the Free plan (2 agents)</li>
                  <li>• All data will be retained for 30 days</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              size="md"
              onClick={handleClose}
              disabled={processing}
              fullWidth
            >
              Keep Subscription
            </Button>
            <Button
              variant="danger"
              size="md"
              onClick={handleFinalCancel}
              loading={processing}
              fullWidth
            >
              Confirm Cancellation
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default CancellationModal;